import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, Check, Crown, Star, Shield,
  CreditCard, AlertTriangle
} from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";

const PLANS = [
  {
    id: 'free',
    name: 'Free Trial',
    price_inr: 0,
    period: '15 days',
    headline: 'Try MISYOU at no cost',
    features: [
      'Report missing persons',
      'Search every active case in India',
      'Submit verified sightings',
      'Standard case visibility for 15 days',
      'Email + push notifications',
    ],
    cta: 'Start free',
    highlight: false,
  },
  {
    id: 'standard',
    name: 'Standard',
    price_inr: 20,
    period: 'per day',
    headline: 'Recommended for active searches',
    features: [
      'Everything in Free Trial',
      'Extended visibility past the 15-day free window',
      'Downloadable invoice for every payment',
      'In-app + browser push notifications',
      'Anti-extortion safeguards (CAPTCHA, scam blocks)',
      'Mandatory police-reporting workflow built-in',
      'Stop auto-deletion after the free 15-day period',
    ],
    cta: 'Subscribe now',
    highlight: true,
  },
];

export default function PricingPage() {
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
              <span className="font-bold text-slate-900">MISYOU</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 pb-24" data-testid="pricing-page">
        {/* Hero */}
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <Badge className="mb-4 bg-emerald-50 text-emerald-700 border-emerald-200 px-3 py-1">
            Simple, transparent pricing
          </Badge>
          <h1 className="text-4xl sm:text-5xl font-black text-slate-900 mb-4">
            Help reunite a family for <span className="text-primary">₹20 a day</span>.
          </h1>
          <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
            Every case starts with a free 15-day listing. Choose Standard at any point
            to keep your missing-person case visible and protected from auto-deletion,
            with an invoice for every payment.
          </p>

          {/* Outcome disclaimer — required for PA/PG chargeback defence.
              MISYOU sells VISIBILITY, not a guarantee of recovery. This
              wording must stay verbatim across pricing, terms, and refund
              policy so "service not provided" chargebacks can be defended. */}
          <div
            className="mt-6 mx-auto max-w-xl rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-left"
            data-testid="pricing-outcome-disclaimer"
          >
            <p className="text-xs text-amber-900 leading-relaxed">
              <strong>Please note:</strong> Your subscription pays for case
              visibility, community reach, and platform infrastructure.
              MISYOU <strong>does not guarantee the recovery, location, or
              safe return</strong> of a missing person — outcomes depend
              on community response and official police action. Full
              details in our{' '}
              <Link to="/terms" className="underline font-medium">
                Terms &amp; Conditions
              </Link>{' '}
              and{' '}
              <Link to="/refund" className="underline font-medium">
                Refund &amp; Cancellation Policy
              </Link>.
            </p>
          </div>
        </div>

        {/* Plan cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-16">
          {PLANS.map((plan) => (
            <Card
              key={plan.id}
              className={`relative ${plan.highlight ? 'border-2 border-primary shadow-xl' : 'border-slate-200'}`}
              data-testid={`pricing-card-${plan.id}`}
            >
              {plan.highlight && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white px-4 py-1">
                  <Star className="w-3.5 h-3.5 mr-1" />
                  Most Popular
                </Badge>
              )}
              <CardContent className="p-8">
                <div className="flex items-center gap-2 mb-3">
                  {plan.highlight ? (
                    <Crown className="w-5 h-5 text-amber-500" />
                  ) : (
                    <Shield className="w-5 h-5 text-slate-500" />
                  )}
                  <h2 className="text-2xl font-bold text-slate-900">{plan.name}</h2>
                </div>
                <p className="text-sm text-slate-500 mb-6">{plan.headline}</p>

                <div className="mb-6">
                  <span className="text-5xl font-black text-slate-900">
                    {plan.price_inr === 0 ? 'Free' : `₹${plan.price_inr}`}
                  </span>
                  {plan.price_inr > 0 && (
                    <span className="text-slate-500 ml-2">/ {plan.period}</span>
                  )}
                  {plan.price_inr === 0 && (
                    <span className="text-slate-500 ml-2">· {plan.period}</span>
                  )}
                </div>

                <ul className="space-y-3 mb-8">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                {plan.id === 'free' ? (
                  <Link to="/register" data-testid={`pricing-cta-${plan.id}`}>
                    <Button variant="outline" className="w-full" size="lg">
                      {plan.cta}
                    </Button>
                  </Link>
                ) : (
                  <Link to="/subscription" data-testid={`pricing-cta-${plan.id}`}>
                    <Button className="w-full bg-primary hover:bg-primary/90" size="lg">
                      {plan.cta}
                    </Button>
                  </Link>
                )}

                <p className="text-xs text-center text-slate-400 mt-3">
                  Pick any number of days · cancel anytime
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Payment methods strip — text-only wordmarks (no third-party logo
            assets) with explicit "Active" / "Verification in progress"
            status. Legal-safe and reviewer-friendly for both Instamojo and
            Paytm KYC teams. Kept in sync with SubscriptionPage.jsx. */}
        <Card className="mb-12 max-w-4xl mx-auto bg-white border-slate-200" data-testid="pricing-payment-partners">
          <CardContent className="p-6">
            <h3 className="text-center text-sm font-semibold text-slate-700 mb-4 uppercase tracking-wider">
              Accepted payment methods
            </h3>
            <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
              <span className="inline-flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 text-sm">
                <CreditCard className="w-4 h-4 text-slate-700" />
                <span className="font-medium">Cards</span>
                <span className="text-slate-400 text-xs">(Visa · Mastercard · RuPay · Amex)</span>
              </span>
            </div>

            <div className="border-t border-slate-100 pt-3">
              <p className="text-[11px] uppercase tracking-widest text-slate-500 text-center mb-2">
                Payment Partner
              </p>
              <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs">
                <div className="flex items-center gap-1.5" data-testid="pricing-partner-paytm">
                  <span className="font-semibold text-slate-700 tracking-tight">Paytm</span>
                  <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[9px] uppercase tracking-wider">
                    Active
                  </Badge>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 text-center mt-2">
                UPI, Cards &amp; Net Banking are processed securely via Paytm Payment Gateway.
              </p>
            </div>

            <p className="text-center text-xs text-slate-400 mt-4">
              All payments are processed by PCI-DSS compliant gateways. MISYOU never stores card details.
            </p>
          </CardContent>
        </Card>

        {/* FAQ */}
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-slate-900 mb-6 text-center">Pricing FAQ</h2>
          <div className="space-y-4">
            <Card><CardContent className="p-5">
              <h3 className="font-semibold text-slate-900 mb-1">Is the free trial really free?</h3>
              <p className="text-sm text-slate-600">Yes — every new case gets 15 days of visibility at no cost. No card needed. After day 15 the listing enters a 15-day grace period (photos blur) before auto-deletion, unless you subscribe.</p>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <h3 className="font-semibold text-slate-900 mb-1">Can I subscribe for just a few days?</h3>
              <p className="text-sm text-slate-600">Yes. Pick any number of days from 1 to 365 on the <Link to="/subscription" className="text-primary underline">Subscribe page</Link>. The total updates instantly (₹20 × days).</p>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <h3 className="font-semibold text-slate-900 mb-1">How do I cancel or refund?</h3>
              <p className="text-sm text-slate-600">Subscriptions are pay-as-you-go — there is nothing to cancel; visibility simply lapses at the end of the paid period. Refunds are governed by our <Link to="/refund" className="text-primary underline">Refund &amp; Cancellation Policy</Link>.</p>
            </CardContent></Card>
            <Card><CardContent className="p-5">
              <h3 className="font-semibold text-slate-900 mb-1">Why do you charge at all?</h3>
              <p className="text-sm text-slate-600">Server costs (image hosting, push delivery, fraud detection, moderation) add up. Charging ₹20/day keeps the lights on while staying affordable for Indian families during a crisis.</p>
            </CardContent></Card>
          </div>

          {/* Cross-links to legal pages — required by Paytm verification checklist */}
          <div className="mt-10 flex flex-wrap justify-center gap-3 text-sm">
            <Link to="/about"><Button variant="ghost" size="sm">About Us</Button></Link>
            <Link to="/contact"><Button variant="ghost" size="sm">Contact Us</Button></Link>
            <Link to="/privacy"><Button variant="ghost" size="sm">Privacy Policy</Button></Link>
            <Link to="/terms"><Button variant="ghost" size="sm">Terms &amp; Conditions</Button></Link>
            <Link to="/refund"><Button variant="ghost" size="sm">Refund &amp; Cancellation</Button></Link>
          </div>

          {/* Trust note */}
          <div className="mt-10 p-5 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-900">
              <strong>Safety note:</strong> MISYOU never asks for money in exchange for information about a missing person.
              Anyone demanding payment from you about a case is a scammer — please report them via the
              {' '}<Link to="/anti-harassment" className="underline font-semibold">Anti-Harassment Policy</Link>.
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
